import { brushDefaults } from './helpers/brushLoader';
import {
  filterClosePoints,
  getStrokeBounds,
  interpolatePointsUniform,
  translatePoints,
} from './helpers/points';

const defaultBrushSettings = brushDefaults.blur || {};
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const stampCache = new Map();

function createBuffer(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function copyRegionToCanvas(sourceCanvas, bounds) {
  const canvas = createBuffer(bounds.width, bounds.height);
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';

  const srcX = Math.max(0, bounds.x);
  const srcY = Math.max(0, bounds.y);
  const dstX = Math.max(0, -bounds.x);
  const dstY = Math.max(0, -bounds.y);
  const drawWidth = Math.max(0, Math.min(sourceCanvas.width - srcX, bounds.width - dstX));
  const drawHeight = Math.max(0, Math.min(sourceCanvas.height - srcY, bounds.height - dstY));

  if (drawWidth > 0 && drawHeight > 0) {
    ctx.drawImage(
      sourceCanvas,
      srcX,
      srcY,
      drawWidth,
      drawHeight,
      dstX,
      dstY,
      drawWidth,
      drawHeight
    );
  }

  return canvas;
}

function prepareSmoothing(ctx) {
  ctx.imageSmoothingEnabled = true;
  if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
}

function getSoftStamp(size) {
  const cacheKey = Math.max(8, Math.round(size));
  if (stampCache.has(cacheKey)) {
    return stampCache.get(cacheKey);
  }

  const canvas = createBuffer(cacheKey, cacheKey);
  const ctx = canvas.getContext('2d');
  const radius = cacheKey * 0.5;
  const gradient = ctx.createRadialGradient(
    radius,
    radius,
    cacheKey * 0.14,
    radius,
    radius,
    radius
  );

  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.45, 'rgba(255,255,255,0.95)');
  gradient.addColorStop(0.8, 'rgba(255,255,255,0.45)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, cacheKey, cacheKey);

  stampCache.set(cacheKey, canvas);
  return canvas;
}

function normalizePoints(points) {
  if (!Array.isArray(points)) return [];
  return points.map((point) => (
    Array.isArray(point)
      ? { x: point[0], y: point[1], pressure: point[2] }
      : point
  ));
}

function drawSoftMask(maskCtx, points, lineWidth, blurStrength, pressureOn) {
  if (!points.length) return;

  const filteredPoints = filterClosePoints(points, Math.max(1.5, lineWidth * 0.08));
  const smoothPoints = interpolatePointsUniform(
    filteredPoints,
    Math.max(1.5, lineWidth * 0.16)
  );

  const largeBrushTightness = clamp(
    1 - Math.max(0, lineWidth - 120) / 900,
    0.82,
    1
  );
  const outerDiameter = lineWidth * clamp(
    (0.9 + blurStrength * 0.12) * largeBrushTightness,
    0.72,
    1.02
  );
  const innerDiameter = lineWidth * clamp(
    (0.56 + blurStrength * 0.1) * largeBrushTightness,
    0.44,
    0.76
  );
  const outerStamp = getSoftStamp(outerDiameter);
  const innerStamp = getSoftStamp(innerDiameter);

  for (let index = 0; index < smoothPoints.length; index++) {
    const point = smoothPoints[index];
    const pressure = pressureOn ? (point.pressure ?? 1) : 1;
    const localScale = pressureOn ? clamp(0.45 + pressure * 0.65, 0.35, 1.15) : 1;

    const outerSize = outerStamp.width * localScale;
    maskCtx.globalAlpha = clamp(0.22 + blurStrength * 0.16, 0.18, 0.46);
    maskCtx.drawImage(
      outerStamp,
      point.x - outerSize * 0.5,
      point.y - outerSize * 0.5,
      outerSize,
      outerSize
    );

    const innerSize = innerStamp.width * localScale;
    maskCtx.globalAlpha = clamp(0.38 + blurStrength * 0.24, 0.32, 0.7);
    maskCtx.drawImage(
      innerStamp,
      point.x - innerSize * 0.5,
      point.y - innerSize * 0.5,
      innerSize,
      innerSize
    );
  }

  maskCtx.globalAlpha = 1;
}

function fitScaleToBudget(width, height, scale, pixelBudget) {
  const safeScale = clamp(scale, 0.08, 1);
  const projectedPixels = width * height * safeScale * safeScale;
  if (!pixelBudget || projectedPixels <= pixelBudget) {
    return safeScale;
  }

  return clamp(Math.sqrt(pixelBudget / Math.max(1, width * height)), 0.08, safeScale);
}

function resampleCanvas(sourceCanvas, targetWidth, targetHeight) {
  const finalWidth = Math.max(1, Math.round(targetWidth));
  const finalHeight = Math.max(1, Math.round(targetHeight));

  if (sourceCanvas.width === finalWidth && sourceCanvas.height === finalHeight) {
    return sourceCanvas;
  }

  let currentCanvas = sourceCanvas;
  let currentWidth = sourceCanvas.width;
  let currentHeight = sourceCanvas.height;

  while (
    currentWidth > finalWidth * 1.35 ||
    currentHeight > finalHeight * 1.35
  ) {
    const nextWidth = Math.max(finalWidth, Math.round(currentWidth * 0.72));
    const nextHeight = Math.max(finalHeight, Math.round(currentHeight * 0.72));
    const nextCanvas = createBuffer(nextWidth, nextHeight);
    const nextCtx = nextCanvas.getContext('2d');
    prepareSmoothing(nextCtx);
    nextCtx.drawImage(
      currentCanvas,
      0,
      0,
      currentWidth,
      currentHeight,
      0,
      0,
      nextWidth,
      nextHeight
    );
    currentCanvas = nextCanvas;
    currentWidth = nextWidth;
    currentHeight = nextHeight;
  }

  if (currentWidth !== finalWidth || currentHeight !== finalHeight) {
    const finalCanvas = createBuffer(finalWidth, finalHeight);
    const finalCtx = finalCanvas.getContext('2d');
    prepareSmoothing(finalCtx);
    finalCtx.drawImage(
      currentCanvas,
      0,
      0,
      currentWidth,
      currentHeight,
      0,
      0,
      finalWidth,
      finalHeight
    );
    return finalCanvas;
  }

  return currentCanvas;
}

function createBlurredRegion(sourceRegion, blurStrength, lineWidth, preview) {
  const width = sourceRegion.width;
  const height = sourceRegion.height;
  const resultCanvas = createBuffer(width, height);
  const resultCtx = resultCanvas.getContext('2d');
  prepareSmoothing(resultCtx);
  const blurPower = clamp(0.14 + blurStrength * 1.22, 0, 1.36);
  const sharedBudget = preview ? 160000 : 180000;

  let primaryScale = clamp(
    0.88 - blurPower * 0.26 - Math.min(0.055, lineWidth / 700),
    0.5,
    0.92
  );
  primaryScale = fitScaleToBudget(
    width,
    height,
    primaryScale,
    sharedBudget
  );

  const primaryWidth = Math.max(1, Math.round(width * primaryScale));
  const primaryHeight = Math.max(1, Math.round(height * primaryScale));
  const primaryCanvas = resampleCanvas(sourceRegion, primaryWidth, primaryHeight);

  resultCtx.globalAlpha = 1;
  resultCtx.drawImage(primaryCanvas, 0, 0, primaryWidth, primaryHeight, 0, 0, width, height);

  const useSecondaryPass = blurPower > 0.34;
  if (useSecondaryPass) {
    let secondaryScale = clamp(
      primaryScale * (0.78 - blurPower * 0.08),
      0.34,
      primaryScale * 0.9
    );
    secondaryScale = fitScaleToBudget(
      width,
      height,
      secondaryScale,
      sharedBudget * 0.75
    );

    const secondaryWidth = Math.max(1, Math.round(width * secondaryScale));
    const secondaryHeight = Math.max(1, Math.round(height * secondaryScale));
    const secondarySource = (
      primaryCanvas.width >= secondaryWidth && primaryCanvas.height >= secondaryHeight
    ) ? primaryCanvas : sourceRegion;
    const secondaryCanvas = resampleCanvas(secondarySource, secondaryWidth, secondaryHeight);

    resultCtx.globalAlpha = clamp(0.12 + blurPower * 0.16, 0.12, 0.3);
    resultCtx.drawImage(
      secondaryCanvas,
      0,
      0,
      secondaryWidth,
      secondaryHeight,
      0,
      0,
      width,
      height
    );
    resultCtx.globalAlpha = 1;
  }

  const useTertiaryPass = blurPower > 0.86;
  if (useTertiaryPass) {
    let tertiaryScale = clamp(primaryScale * 0.54, 0.28, 0.42);
    tertiaryScale = fitScaleToBudget(width, height, tertiaryScale, sharedBudget * 0.56);

    const tertiaryWidth = Math.max(1, Math.round(width * tertiaryScale));
    const tertiaryHeight = Math.max(1, Math.round(height * tertiaryScale));
    const tertiaryCanvas = resampleCanvas(primaryCanvas, tertiaryWidth, tertiaryHeight);

    resultCtx.globalAlpha = clamp((blurPower - 0.82) * 0.22, 0, 0.11);
    resultCtx.drawImage(
      tertiaryCanvas,
      0,
      0,
      tertiaryWidth,
      tertiaryHeight,
      0,
      0,
      width,
      height
    );
    resultCtx.globalAlpha = 1;
  }

  return resultCanvas;
}

export async function drawBlurStroke({
  lineWidth,
  points,
  sets = {},
}, context, params = {}) {
  const normalizedPoints = normalizePoints(points);
  if (normalizedPoints.length === 0) return;

  const settings = Object.assign({}, defaultBrushSettings, sets);
  const {
    blurStrength = settings.intensity ?? 0.55,
    pressureOn = false,
  } = settings;

  const effectiveStrength = clamp(blurStrength, 0, 1);
  if (effectiveStrength <= 0) return;
  const renderStrength = clamp(0.08 + effectiveStrength * 1.18, 0, 1.26);

  const sourceCtx = params.sourceCtx || context;
  const sourceCanvas = sourceCtx?.canvas;
  if (!sourceCanvas) return;

  const preview = !!params.preview;
  const paddingTightness = clamp(
    1 - Math.max(0, lineWidth - 120) / 1100,
    0.84,
    1
  );
  const padding = lineWidth * (0.62 + renderStrength * 1.08) * paddingTightness;
  const bounds = getStrokeBounds(normalizedPoints, lineWidth * 1.4, padding, 64, 64);
  if (!bounds) return;

  const localPoints = translatePoints(normalizedPoints, -bounds.x, -bounds.y);
  const sourceRegion = copyRegionToCanvas(sourceCanvas, bounds);
  const blurredRegion = createBlurredRegion(sourceRegion, renderStrength, lineWidth, preview);

  const maskCanvas = createBuffer(bounds.width, bounds.height);
  const maskCtx = maskCanvas.getContext('2d');
  maskCtx.imageSmoothingEnabled = true;
  if ('imageSmoothingQuality' in maskCtx) maskCtx.imageSmoothingQuality = 'high';
  drawSoftMask(maskCtx, localPoints, lineWidth, renderStrength, pressureOn);

  const blurredCtx = blurredRegion.getContext('2d');
  blurredCtx.globalCompositeOperation = 'destination-in';
  blurredCtx.drawImage(maskCanvas, 0, 0);
  blurredCtx.globalCompositeOperation = 'source-over';

  context.save();
  context.globalAlpha = clamp(0.32 + renderStrength * 0.72, 0.28, 1);
  context.drawImage(
    blurredRegion,
    0,
    0,
    bounds.width,
    bounds.height,
    bounds.x,
    bounds.y,
    bounds.width,
    bounds.height
  );
  context.restore();
}
